import { defineComponent as _defineComponent } from 'vue'
import {
  Employee,
  LocationStatusEnum,
  ScheduleSettings,
  Shift,
} from '@/../api/v1';
import Button from '@/components/buttons/Button.vue';
import ButtonMenu from '@/components/buttons/ButtonMenu.vue';
import EmployeeAvatar from '@/components/employee/EmployeeAvatar.vue';
import InlineDialog from '@/components/info/InlineDialog.vue';
import BubbleIcon from '@/components/interface/BubbleIcon.vue';
import Chip from '@/components/interface/Chip.vue';
import i18n from '@/i18n';
import { dateTimeFormats } from '@/lang/dateTimeFormats';
import {
  ShiftActionExcludeOptions,
  ShiftActionType,
  useShiftActions,
} from '@/lib/actions/shiftActionsComposable';
import { fullName } from '@/lib/employee/employeeFunctions';
import { Icon } from '@/lib/enum/Icon';
import {
  companyCanAccess,
  Feature,
} from '@/lib/permission/companyAccessFeatures';
import { userCan } from '@/lib/permission/userCan';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import { useRealtimeQueryById } from '@/lib/realtime/weak/realtimeHooks';
import { EmitCreateShiftData } from '@/lib/schedule-v2/scheduleV2Actions';
import { presentShiftTimes } from '@/lib/schedule-v2/scheduleV2Functions';
import {
  conflictWarningMessage,
  tagVisibilityFilter,
} from '@/lib/schedule-v2/shiftV2Functions';
import { isUnassignedAndNotOpen } from '@/lib/schedule-v2/shiftV2Status';
import {
  getBreakEndTime,
  getBreakTypeIcon,
  getBreakTypeName,
} from '@/lib/scheduledBreak/scheduledBreakFunctions';
import store from '@/store';
import { getMapFromUniqueEntities } from '@/util/entityFunctions';
import { keyOfFirstTruthyValue } from '@/util/objectFunctions';
import { computed } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ShiftDetailsSheetContent',
  props: {
    shift: null,
    employeeActions: { type: Boolean, default: false },
    disableActions: { type: Boolean, default: false },
    excludedActions: { default: () => [] },
    showManagementDetails: { type: Boolean, default: true },
    matchingShiftIds: { default: () => [] }
  },
  emits: ["input", "close", "createShift"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input'): void;
  (e: 'close'): void;
  (e: 'createShift', value: EmitCreateShiftData): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { shift: Shift, employeeActions: boolean, disableActions: boolean, excludedActions: ShiftActionExcludeOptions, showManagementDetails: boolean, matchingShiftIds: number[] }

const companyHasShiftAreas = computed(() =>
  companyCanAccess(Feature.ShiftAreas),
);
const loggedInEmployee = computed<Employee>(
  () => store.getters.loggedInEmployee,
);
const scheduleSettings = computed<ScheduleSettings>(
  () => store.state.settings.scheduleSettings,
);

const timezone = computed<string>(() => store.getters.timezone);





const locations = realtimeQuery(Entity.Location).fetch();

const totalActiveLocations = computed<number>(
  () =>
    locations.data.filter((l) => l.status === LocationStatusEnum.Active).length,
);

const { entity: employee, response: employeeQuery } = useRealtimeQueryById(
  Entity.Employee,
  () => props.shift.employeeId,
);

const { entity: jobRole, response: jobRoleQuery } = useRealtimeQueryById(
  Entity.JobRole,
  () => props.shift.jobRoleId,
);

const { entity: location, response: locationQuery } = useRealtimeQueryById(
  Entity.Location,
  () => props.shift.locationId,
  {},
  () => locations,
);

const cardLoading = computed<boolean>(
  () =>
    locations.isLoading ||
    employeeQuery.value.isLoading ||
    jobRoleQuery.value.isLoading ||
    locationQuery.value.isLoading,
);

const tags = computed(() =>
  realtimeQuery(Entity.Tag).whereIn('id', props.shift.tagIds).fetch(),
);

const visibleTags = computed(() =>
  tags.value.data.filter(tagVisibilityFilter(userCan.manageShifts())),
);

const shiftAreas = computed(() =>
  realtimeQuery(Entity.ShiftArea)
    .whereIn(
      'id',
      props.shift.shiftAreaSessions.map(({ shiftAreaId }) => shiftAreaId),
    )
    .fetch(),
);

const sortedSessionsList = computed<
  {
    id: string;
    name: string;
    colour?: string;
    duration?: number;
    icon?: string;
    dateRange: [Date, Date] | null;
  }[]
>(() => {
  let shiftAreaSessions = [];

  if (companyHasShiftAreas.value) {
    const shiftAreasMap = getMapFromUniqueEntities(shiftAreas.value.data);
    shiftAreaSessions =
      props.shift.shiftAreaSessions
        ?.map((areaSession) => {
          const area = shiftAreasMap.get(areaSession.shiftAreaId);

          if (!area) {
            return null;
          }

          return {
            id: `area-${area.id}-${areaSession.id}`,
            name: area.name,
            colour: area.colour,
            dateRange: [areaSession.startTime, areaSession.endTime],
          };
        })
        .filter(Boolean) ?? [];
  }

  return [
    ...shiftAreaSessions,
    ...props.shift.scheduledBreaks.map((b) => ({
      id: `break-${b.id}`,
      name: getBreakTypeName(b, scheduleSettings.value),
      icon: getBreakTypeIcon(b.paid),
      duration: b.durationInMinutes,
      dateRange: b.startsAt
        ? [b.startsAt, getBreakEndTime(b, timezone.value)]
        : null,
    })),
  ].sort((a, b) => {
    if (!a.dateRange) return -1;
    if (!b.dateRange) return 1;
    return a.dateRange[0] - b.dateRange[0];
  });
});

const showJobRole = computed<boolean>(
  () => props.shift.showJobRole || userCan.manageShifts(),
);

const jobRoleBg = computed<string>(() => {
  if (!showJobRole.value) return 'transparent';

  const { colour } = jobRole.value;
  return props.shift.publishedAt
    ? colour
    : `repeating-linear-gradient(-45deg, ${colour}, ${colour} 4px, ${colour}99 4px, ${colour}99 8px)`;
});

const status = computed<string>(() => {
  if (isUnassignedAndNotOpen(props.shift) || !userCan.manageShifts()) {
    return '';
  }

  if (props.shift.publishedAt) {
    return scheduleSettings.value.employeesMustAcknowledgeShifts &&
      props.shift.employeeId
      ? keyOfFirstTruthyValue({
          [i18n.tc('label.acknowledged')]: !!props.shift.acknowledgedAt,
          [i18n.tc('label.notAcknowledged')]: !props.shift.acknowledgedAt,
        })
      : i18n.tc('label.published');
  }

  return i18n.tc('label.unpublished');
});

const shiftActions = useShiftActions(
  computed(() => ({
    useActions: !props.disableActions,
    shift: props.shift,
    employee: employee.value,
    employeeActions: props.employeeActions,
    excludedActions: props.excludedActions,
    emit,
  })),
);

const mainAction = computed<ShiftActionType | undefined>(() => {
  return shiftActions.value.find((item) => item.useAsMain);
});

const conflictWarning = computed<string>(() => {
  return props.showManagementDetails
    ? conflictWarningMessage(props.shift, employee.value)
    : '';
});

return { __sfc: true,companyHasShiftAreas, loggedInEmployee, scheduleSettings, timezone, props, emit, locations, totalActiveLocations, employee, employeeQuery, jobRole, jobRoleQuery, location, locationQuery, cardLoading, tags, visibleTags, shiftAreas, sortedSessionsList, showJobRole, jobRoleBg, status, shiftActions, mainAction, conflictWarning, Button, ButtonMenu, EmployeeAvatar, InlineDialog, BubbleIcon, Chip, dateTimeFormats, fullName, Icon, userCan, presentShiftTimes, keyOfFirstTruthyValue }
}

})